// src/components/admin/AdminDashboard/AdminDashboard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Package, Calendar, Users, Clock, UserX, Shield, ArrowRight, IndianRupee,
} from 'lucide-react';

// MUI from central lib file
import {
  Skeleton, Box, LinearProgress, Typography,
  Paper, Stack, Divider, Alert,
} from '../../lib/mui';

// Direct imports from @mui/material
import {
  Collapse, ToggleButton, ToggleButtonGroup,
  Container, Card, CardContent, Button,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';

import { useAuth }           from '../../contexts/AuthContext';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';

// ─── Helper: Format currency in short form (e.g., 20L, 15K) ──────────────────

const formatCurrencyShort = (value) => {
  if (value === undefined || value === null) return '0';
  
  const num = Number(value);
  if (isNaN(num)) return '0';
  
  if (num >= 10000000) {
    return (num / 10000000).toFixed(1) + 'Cr';
  }
  if (num >= 100000) {
    return (num / 100000).toFixed(1) + 'L';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon: Icon, color, subtitle, loading }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 2.5,
      borderLeft: `4px solid ${color}`,
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      },
      height: '100%',
    }}
  >
    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#6b7280', 
              fontWeight: 500, 
              mb: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: '#111827', 
              lineHeight: 1.2,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            {loading ? '...' : value}
          </Typography>
          {subtitle && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#9ca3af', 
                mt: 0.5, 
                display: 'block',
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Icon 
          size={40} 
          color={color} 
          style={{ 
            opacity: 0.25, 
            flexShrink: 0,
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
          }} 
        />
      </Stack>
    </CardContent>
  </Card>
);

// ─── Landing Page ─────────────────────────────────────────────────────────────

const LandingView = ({ onProceed }) => (
  <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: { xs: 3, sm: 5 }, px: { xs: 1.5, sm: 2.5 } }}>
    <Box
      sx={{
        textAlign: 'center',
        mb: { xs: 4, sm: 7.5 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 2, sm: 2.5 },
      }}
    >
      <Box
        component="img"
        src="/logo.png"
        alt="NIYANTRIX Logo"
        sx={{ 
          width: { xs: '70vw', sm: '50vw', md: '35vw', lg: '25vw' }, 
          textAlign: 'center',
          maxWidth: '400px',
        }}
      />
      <Typography variant="h4" sx={{ 
        color: '#1f2937', 
        fontWeight: 700,
        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
      }}>
        NIYANTRIX Asset Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
        Scalable Solutions for Modern Enterprises
      </Typography>
      <Button
        variant="contained"
        onClick={onProceed}
        endIcon={<ArrowRight />}
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 1.5, sm: 1.75 },
          borderRadius: 6,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          '&:hover': { bgcolor: '#1565c0' },
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        Dashboard
      </Button>
    </Box>

    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {[
          { title: 'QR Automation',     desc: 'Automated QR code generation and scanning.'          },
          { title: 'AI Maintenance',    desc: 'AI-driven predictive maintenance and renewals.'       },
          { title: 'RBAC',              desc: 'Role-based access for Super Admin, Admin, and Users.' },
          { title: 'Location Tracking', desc: 'Location and department-based tracking.'              },
          { title: 'Audit Logs',        desc: 'Integrated audit logs and activity monitoring.'       },
        ].map((feat, i) => (
          <Card
            key={i}
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              mb: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}>
              {feat.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {feat.desc}
            </Typography>
          </Card>
        ))}
      </Box>
    </Box>

    <Box
      sx={{
        textAlign: 'center',
        mt: { xs: 5, sm: 10 },
        pt: { xs: 3, sm: 5 },
        borderTop: '1px solid #e5e7eb',
        px: { xs: 2, sm: 0 },
      }}
    >
      <Typography variant="h6" sx={{ 
        fontWeight: 600, 
        mb: 1,
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
      }}>
        Connect With Our Team
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
        Dedicated Support: <strong>+91 6360919373</strong>
      </Typography>
      <Typography variant="body2" sx={{ 
        color: '#4b5563', 
        fontSize: { xs: '0.75rem', sm: '0.9rem' },
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: { xs: 1, sm: 2 },
      }}>
        <Box component="span">Schedule Live Demo</Box>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>|</Box>
        <Box component="span">Enterprise API</Box>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>|</Box>
        <Box component="span">Partnership Opportunities</Box>
      </Typography>
    </Box>
  </Box>
);

// ─── Skeleton Loading Screen ──────────────────────────────────────────────────

const DashboardSkeleton = ({ initProgress, initProgressLabel }) => (
  <Box sx={{ padding: { xs: '16px', sm: '24px' } }}>
    {initProgress > 0 && initProgress < 100 && (
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={initProgress}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: '#e2e8f0',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #3B82F6, #06B6D4)',
            },
          }}
        />
        <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
          {initProgressLabel || 'Initializing...'}
        </Typography>
      </Box>
    )}

    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
      <Box>
        <Skeleton variant="text" width={200} height={36} />
        <Skeleton variant="text" width={280} height={20} />
      </Box>
      <Skeleton variant="rounded" width={{ xs: '100%', sm: 260 }} height={44} />
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: { xs: 1.5, sm: 2 },
        mb: 2,
      }}
    >
      {Array.from({ length: 4 }, (_, i) => (
        <Box
          key={i}
          sx={{
            background: '#fff',
            borderRadius: '12px',
            padding: { xs: '16px', sm: '20px' },
            borderLeft: '4px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={60}  height={32} sx={{ my: 0.5 }} />
            <Skeleton variant="text" width={100} height={12} />
          </Box>
          <Skeleton variant="circular" width={36} height={36} />
        </Box>
      ))}
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 1.5, sm: 2 },
        mb: 2,
      }}
    >
      {Array.from({ length: 2 }, (_, i) => (
        <Box
          key={i}
          sx={{
            background: '#fff',
            borderRadius: '12px',
            padding: { xs: '16px', sm: '20px' },
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Skeleton variant="text" width={140} height={20} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={350} />
        </Box>
      ))}
    </Box>

    <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3 }} />
  </Box>
);

// ─── Asset Distribution Insights ──────────────────────────────────────────────

const AssetDistributionInsights = ({
  assetsByCategory = [],
  assetsBySubCategory = [],
  assetsByBrand = [],
  assetsByLocation = [],
  assetsByStatus = [],
  totalAssets,
}) => {
  const [showDistribution, setShowDistribution] = useState(false);
  const [distView, setDistView] = useState('category');

  const distributionData = useMemo(() => {
    if (distView === 'category') {
      return (assetsByCategory || []).map(i => ({
        label: i.fullName || i.name,
        count: i.value,
      }));
    }
    if (distView === 'subcategory') {
      return (assetsBySubCategory || []).map(i => ({
        label: i.fullName || i.name,
        count: i.value,
      }));
    }
    if (distView === 'brand') {
      return (assetsByBrand || []).map(i => ({
        label: i.fullName || i.name,
        count: i.value,
      }));
    }
    if (distView === 'status') {
      return (assetsByStatus || []).map(i => ({
        label: i.status,
        count: i.count,
      }));
    }
    return (assetsByLocation || []).map(i => ({
      label: i.location,
      count: i.assets,
    }));
  }, [distView, assetsByCategory, assetsBySubCategory, assetsByBrand, assetsByLocation, assetsByStatus]);

  const viewLabel =
    distView === 'category' ? 'categories' :
    distView === 'subcategory' ? 'subcategories' :
    distView === 'brand' ? 'brands' :
    distView === 'status' ? 'statuses' :
    'locations';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        mb: 3,
        mt: 3,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        onClick={() => setShowDistribution(!showDistribution)}
        sx={{
          px: { xs: 2, sm: 3 }, 
          py: { xs: 1.5, sm: 2 },
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          bgcolor: showDistribution ? 'transparent' : '#FAFBFC',
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: '#F8FAFC' },
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ 
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
          }}>
            Asset Distribution Insights
          </Typography>
          {!showDistribution && (
            <Typography variant="caption" color="text.secondary" sx={{ 
              display: { xs: 'none', sm: 'block' },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}>
              Click to view visual distribution charts by Category, Subcategory, Brand, Status, or Location
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography
            variant="body2"
            color="primary"
            fontWeight={600}
            sx={{ 
              display: { xs: 'none', sm: 'block' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {showDistribution ? 'Hide Insights' : 'Show Insights'}
          </Typography>
          {showDistribution
            ? <KeyboardArrowUpIcon color="primary" />
            : <KeyboardArrowDownIcon color="primary" />}
        </Box>
      </Box>

      <Collapse in={showDistribution}>
        <Divider />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={{ xs: 2, sm: 2 }}
            sx={{ mb: 3 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {distributionData.length} unique {viewLabel}
            </Typography>

            <Box sx={{ 
              maxWidth: '100%', 
              overflowX: 'auto', 
              py: 0.5,
              width: { xs: '100%', sm: 'auto' },
            }}>
              <ToggleButtonGroup
                value={distView}
                exclusive
                onChange={(e, val) => val && setDistView(val)}
                size="small"
                sx={{
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  flexWrap: 'nowrap',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1, sm: 2 }, 
                    py: { xs: 0.5, sm: 0.75 },
                    border: '1px solid',
                    borderColor: 'divider',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      bgcolor: '#1565C0',
                      color: 'white',
                      '&:hover': { bgcolor: '#1565C0' },
                    },
                  },
                }}
              >
                <ToggleButton value="category">Category</ToggleButton>
                <ToggleButton value="subcategory">Subcategory</ToggleButton>
                <ToggleButton value="brand">Brand</ToggleButton>
                <ToggleButton value="status">Status</ToggleButton>
                <ToggleButton value="location">Location</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>

          {distributionData.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: '1fr 1fr', 
                  md: '1fr 1fr 1fr' 
                },
                gap: { xs: 2, sm: 3 },
                maxHeight: { xs: 400, sm: 320 },
                overflowY: 'auto',
                pr: 1,
                width: '100%',
              }}
            >
              {distributionData.map((item) => {
                const percent = totalAssets > 0 ? (item.count / totalAssets) * 100 : 0;
                return (
                  <Box key={item.label} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 0.75 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.primary"
                        noWrap
                        sx={{ 
                          maxWidth: '55%',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                        title={item.label}
                      >
                        {item.label}
                      </Typography>
                      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          }}
                        >
                          {item.count}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: '#1565C0',
                            minWidth: { xs: 36, sm: 44 },
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          }}
                        >
                          {percent.toFixed(1)}%
                        </Typography>
                      </Stack>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      sx={{
                        height: { xs: 4, sm: 6 },
                        borderRadius: 999,
                        bgcolor: '#F1F5F9',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          bgcolor: '#1565C0',
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No distribution data available
            </Alert>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Custom Tooltip for Bar Chart ─────────────────────────────────────────────

const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: 'white',
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #e5e7eb',
          minWidth: 150,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
          {data.fullName || data.name || data.location || label}
        </Typography>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Assets:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
            {data.value || data.assets || 0}
          </Typography>
        </Stack>
        {data.totalValue !== undefined && data.totalValue !== null && (
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Value:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
              ₹{data.totalValue.toLocaleString('en-IN')}
            </Typography>
          </Stack>
        )}
      </Box>
    );
  }
  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const [showDashboard, setShowDashboard] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  
  // Tab selector state for Left Chart: 'category' | 'subcategory'
  const [activeTab, setActiveTab] = useState('category');

  const { currentUser, loading: authLoading } = useAuth();

  const {
    loading,
    initProgress,
    initProgressLabel,
    companyData,
    dashboardData,
    assetsByCategory = [],
    assetsBySubCategory = [],
    assetsByBrand = [],
    assetsByLocation = [],
    assetsByStatus: hookAssetsByStatus = [],
  } = useAdminDashboard(showDashboard) || {};

  const assetsByStatus = useMemo(() => {
    if (hookAssetsByStatus?.length) return hookAssetsByStatus;

    const total = dashboardData?.totalAssets ?? 0;
    const unassigned = dashboardData?.unassignedAssets ?? 0;
    const assigned = Math.max(0, total - unassigned);

    return [
      { status: 'Assigned',   count: assigned },
      { status: 'Unassigned', count: unassigned },
    ].filter(i => i.count > 0 || total === 0);
  }, [hookAssetsByStatus, dashboardData]);

  // Dynamically switch data feed based on active tab selection
  const tabbedChartData = useMemo(() => {
    let data = [];
    if (activeTab === 'subcategory') {
      data = assetsBySubCategory;
    } else {
      data = assetsByCategory;
    }
    
    return data.map(item => ({
      ...item,
      totalValue: item.totalValue || item.value * 5000 
    }));
  }, [activeTab, assetsByCategory, assetsBySubCategory]);

  const locationChartData = useMemo(() => {
    return (assetsByLocation || []).map(item => ({
      ...item,
      totalValue: item.totalValue || item.assets * 5000 
    }));
  }, [assetsByLocation]);

  const totalAssetValue = useMemo(() => {
    if (dashboardData?.totalValue !== undefined && dashboardData?.totalValue !== null) {
      return dashboardData.totalValue;
    }
    
    let total = 0;
    assetsByCategory.forEach(cat => {
      if (cat.value && cat.averageValue) {
        total += cat.value * cat.averageValue;
      } else if (cat.value) {
        total += cat.value * 5000;
      }
    });
    return total || 0;
  }, [dashboardData, assetsByCategory]);

  const tabbedChartColor = useMemo(() => {
    if (activeTab === 'subcategory') return '#E94B8A';
    return '#3B82F6';
  }, [activeTab]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Guard Renders ──────────────────────────────────────────────────────────

  if (authLoading || loading) {
    return (
      <DashboardSkeleton
        initProgress={initProgress}
        initProgressLabel={initProgressLabel}
      />
    );
  }

  if (!currentUser) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="warning">Please log in to view the dashboard.</Alert>
      </Box>
    );
  }

  // CRITICAL FIX: Check if currentUser has a companyId before checking companyData
  if (!currentUser.companyId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">
          Your account is not associated with any company (Missing companyId on user profile). Please contact support.
        </Alert>
      </Box>
    );
  }

  // If companyId exists but companyData failed to load (e.g., network error)
  if (!companyData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">
          Failed to load company details for ID: {currentUser.companyId}. Please try refreshing the page or contact support.
        </Alert>
      </Box>
    );
  }

  if (!showDashboard) {
    return <LandingView onProceed={() => setShowDashboard(true)} />;
  }

  // ── Main Render ────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
        p: { xs: 1.5, sm: 2, md: 2.5 },
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Container 
        maxWidth={false} 
        sx={{ 
          maxWidth: '1800px !important',
          px: { xs: 0, sm: 1, md: 2 },
          width: '100%',
        }}
      >

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 2, sm: 2.5 }}
          sx={{ mb: { xs: 2.5, sm: 3.75 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ 
                fontWeight: 700, 
                color: '#1f2937', 
                mb: 0.5,
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                wordBreak: 'break-word',
              }}
            >
              Admin Dashboard
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#6b7280',
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                wordBreak: 'break-word',
              }}
            >
              {companyData?.name} • Welcome back,{' '}
              {currentUser?.displayName || currentUser?.email || 'User'}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 1.5 },
              borderRadius: 2.5,
              boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
              width: { xs: '100%', sm: 'auto' },
              flexShrink: 0,
            }}
          >
            <Stack spacing={{ xs: 0.5, sm: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Calendar size={18} color="#4b5563" />
                <Typography
                  variant="body2"
                  sx={{ 
                    fontWeight: 500, 
                    color: '#374151',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  }}
                >
                  {currentDateTime.toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long',
                    day: 'numeric', year: 'numeric',
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Clock size={18} color="#4b5563" />
                <Typography
                  variant="body2"
                  sx={{ 
                    fontWeight: 600, 
                    color: '#111827',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  }}
                >
                  {currentDateTime.toLocaleTimeString('en-US', {
                    hour: '2-digit', minute: '2-digit',
                    second: '2-digit', hour12: true,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>

        {/* Stat Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr 1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(5, 1fr)' 
            },
            gap: { xs: 1.5, sm: 2, md: 2.5 },
            mb: { xs: 2.5, sm: 3.75 },
          }}
        >
          <StatCard
            title="Total Assets"
            value={(dashboardData?.totalAssets ?? 0).toLocaleString()}
            icon={Package}
            color="#3B82F6"
            loading={loading}
            subtitle={`For ${companyData?.name}`}
          />
          <StatCard
            title="Unassigned Assets"
            value={(dashboardData?.unassignedAssets ?? 0).toLocaleString()}
            icon={UserX}
            color="#EF4444"
            loading={loading}
            subtitle={
              dashboardData?.totalAssets > 0
                ? `${(((dashboardData?.unassignedAssets ?? 0) / dashboardData.totalAssets) * 100).toFixed(1)}% of total`
                : '0% of total'
            }
          />
          <StatCard
            title="Warranty Expiring"
            value={(dashboardData?.warrantyExpiring ?? 0).toLocaleString()}
            icon={Shield}
            color="#F59E0B"
            loading={loading}
            subtitle="Within 30 days"
          />
          <StatCard
            title="Total Users"
            value={dashboardData?.usersCount ?? 0}
            icon={Users}
            color="#06B6D4"
            loading={loading}
            subtitle={`in ${companyData?.name}`}
          />
          <StatCard
            title="Total Asset Value"
            value={`₹${formatCurrencyShort(totalAssetValue)}`}
            icon={IndianRupee}
            color="#8B5CF6"
            loading={loading}
            subtitle={`${(dashboardData?.totalAssets ?? 0)} assets`}
          />
        </Box>

        {/* Dynamic symmetric Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 1.5, sm: 2, md: 3 },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {/* Chart Card 1: Tabbed Asset Metrics Breakdown with Total Value */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: 2.5,
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{ mb: { xs: 2, sm: 2.5 } }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' }, 
                    fontWeight: 600, 
                    color: '#1f2937' 
                  }}
                >
                  Asset Metrics Breakdown
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.25 }}
                >
                  Hover bars for asset count & total value
                </Typography>
              </Box>

              <ToggleButtonGroup
                value={activeTab}
                exclusive
                onChange={(e, val) => val && setActiveTab(val)}
                size="small"
                sx={{
                  bgcolor: '#f3f4f6',
                  p: 0.5,
                  borderRadius: 2,
                  border: 'none',
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.25, sm: 0.5 },
                    color: '#6b7280',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      bgcolor: '#fff',
                      color: '#111827',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': { bgcolor: '#fff' },
                    },
                  },
                }}
              >
                <ToggleButton value="category">Category</ToggleButton>
                <ToggleButton value="subcategory">Subcategory</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {!tabbedChartData?.length ? (
              <Box
                sx={{
                  height: { xs: 350, sm: 420, md: 480 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af',
                  border: '1px dashed #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  No {activeTab} data available on assets
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                <Box
                  sx={{
                    minWidth: Math.max(300, (tabbedChartData?.length ?? 0) * 65),
                    height: { xs: 350, sm: 420, md: 480 },
                    width: '100%',
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tabbedChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis
                        dataKey={(row) => row.fullName || row.name}
                        angle={-45}
                        textAnchor="end"
                        height={140}
                        interval={0}
                        tick={{ fontSize: 12, fill: '#4B5563' }}
                        axisLine={{ stroke: '#D1D5DB' }}
                        tickLine={false}
                      />
                      <YAxis 
                        allowDecimals={false} 
                        tick={{ fontSize: 12, fill: '#6B7280' }} 
                        axisLine={false} 
                        tickLine={false}
                        label={{ 
                          value: 'Number of Assets', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { fontSize: 12, fill: '#6B7280' }
                        }}
                      />
                      <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }} />
                      <Bar 
                        dataKey="value" 
                        radius={[6, 6, 0, 0]} 
                        maxBarSize={50}
                        name="Assets"
                      >
                        {tabbedChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill || tabbedChartColor} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                
                {/* Legend for Total Value */}
                {tabbedChartData.some(item => item.totalValue !== undefined) && (
                  <Stack 
                    direction="row" 
                    spacing={2} 
                    justifyContent="center" 
                    sx={{ 
                      mt: 1, 
                      pt: 1, 
                      borderTop: '1px solid #e5e7eb',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 12, height: 12, bgcolor: tabbedChartColor, borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Asset Count
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        bgcolor: '#059669', 
                        borderRadius: 0.5,
                        border: '1px dashed #047857',
                      }} />
                      <Typography variant="caption" color="text.secondary">
                        Total Value (₹) - hover bars to view
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Box>
            )}
          </Paper>

          {/* Chart Card 2: Location Distribution with Total Value */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: 2.5,
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
                  fontWeight: 600,
                  color: '#1f2937',
                }}
              >
                Assets by Location
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.25, mb: { xs: 2, sm: 2.5 } }}
              >
                Hover bars for asset count & total value by location
              </Typography>
            </Box>
            
            <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
              <Box
                sx={{
                  minWidth: Math.max(300, (locationChartData?.length ?? 0) * 65),
                  height: { xs: 350, sm: 420, md: 480 },
                  width: '100%',
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={locationChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis
                      dataKey={(row) => row.fullName || row.location}
                      angle={-45}
                      textAnchor="end"
                      height={140}
                      interval={0}
                      tick={{ fontSize: 12, fill: '#4B5563' }}
                      axisLine={{ stroke: '#D1D5DB' }}
                      tickLine={false}
                    />
                    <YAxis 
                      allowDecimals={false} 
                      tick={{ fontSize: 12, fill: '#6B7280' }} 
                      axisLine={false} 
                      tickLine={false}
                      label={{ 
                        value: 'Number of Assets', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fontSize: 12, fill: '#6B7280' }
                      }}
                    />
                    <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }} />
                    <Bar 
                      dataKey="assets" 
                      fill="#3B82F6" 
                      radius={[6, 6, 0, 0]} 
                      maxBarSize={50}
                      name="Assets"
                    >
                      {locationChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill || '#3B82F6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              
              {/* Legend for Total Value */}
              {locationChartData.some(item => item.totalValue !== undefined) && (
                <Stack 
                  direction="row" 
                  spacing={2} 
                  justifyContent="center" 
                  sx={{ 
                    mt: 1, 
                    pt: 1, 
                    borderTop: '1px solid #e5e7eb',
                    flexWrap: 'wrap',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 12, height: 12, bgcolor: '#3B82F6', borderRadius: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      Asset Count
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      bgcolor: '#059669', 
                      borderRadius: 0.5,
                      border: '1px dashed #047857',
                    }} />
                    <Typography variant="caption" color="text.secondary">
                      Total Value (₹) - hover bars to view
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Asset Distribution Insights */}
        <AssetDistributionInsights
          assetsByCategory={assetsByCategory}
          assetsBySubCategory={assetsBySubCategory}
          assetsByBrand={assetsByBrand}
          assetsByLocation={assetsByLocation}
          assetsByStatus={assetsByStatus}
          totalAssets={dashboardData?.totalAssets ?? 0}
        />

      </Container>
    </Box>
  );
};

export default AdminDashboard;